import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiDelete, apiPut, apiPatch, ApiClientError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useToast } from '@/hooks/use-toast';
import type {
    User,
    AuthResponse,
    LoginInput,
    RegisterInput,
    UpdatePasswordInput,
    UpdateProfileInput,
    ForgotPasswordInput,
    ResetPasswordInput,
    Session,
} from '@/lib/schemas';

// Query keys
export const authKeys = {
    all: ['auth'] as const,
    me: () => [...authKeys.all, 'me'] as const,
    sessions: () => [...authKeys.all, 'sessions'] as const,
};

// Fetch current user
export function useCurrentUser() {
    const { setUser, setLoading } = useAuthStore();

    return useQuery({
        queryKey: authKeys.me(),
        queryFn: async () => {
            try {
                const user = await apiGet<User>('/auth/me');
                setUser(user);
                return user;
            } catch (error) {
                if (error instanceof ApiClientError && error.statusCode === 401) {
                    setUser(null);
                    return null;
                }
                throw error;
            } finally {
                setLoading(false);
            }
        },
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchInterval: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
    });
}

// Login mutation
export function useLogin(redirectTo = '/dashboard') {
    const queryClient = useQueryClient();
    const { setUser } = useAuthStore();
    const navigate = useNavigate();
    const { toast } = useToast();

    return useMutation({
        mutationFn: async (data: LoginInput) => {
            return apiPost<AuthResponse>('/auth/login', data);
        },
        onSuccess: (data) => {
            setUser(data.user);
            queryClient.setQueryData(authKeys.me(), data.user);
            toast({
                title: 'Welcome back!',
                description: `Signed in as ${data.user.email}`,
            });
            navigate(redirectTo);
        },
        onError: (error: Error) => {
            // R24: an unverified account is not a generic failure — the
            // login page renders a dedicated "confirm your e-mail" panel
            // with a resend action, so skip the toast here.
            if (error instanceof ApiClientError && error.code === 'EMAIL_NOT_VERIFIED') return;
            const message =
                error instanceof ApiClientError
                    ? error.message
                    : 'An unexpected error occurred';
            toast({
                title: 'Login failed',
                description: message,
                variant: 'destructive',
            });
        },
    });
}

// Register mutation.
// R24: registration no longer signs the user in — the backend creates
// the account unverified and e-mails a confirmation link. The caller
// shows the "check your inbox" screen instead of navigating away.
export interface RegisterResult {
    user: { id: string; email: string; name: string; avatarUrl?: string | null; createdAt: string };
    verificationRequired: true;
    verificationEmailSent: boolean;
}

export function useRegister() {
    const { toast } = useToast();

    return useMutation({
        mutationFn: async (data: RegisterInput) => {
            return apiPost<RegisterResult>('/auth/register', data);
        },
        onError: (error: Error) => {
            const message =
                error instanceof ApiClientError
                    ? error.message
                    : 'An unexpected error occurred';
            toast({
                title: 'Registration failed',
                description: message,
                variant: 'destructive',
            });
        },
    });
}

// R24: redeem the e-mail verification token and sign in.
export function useVerifyEmail(redirectTo = '/dashboard') {
    const queryClient = useQueryClient();
    const { setUser } = useAuthStore();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async (token: string) => {
            return apiPost<{ message: string; user: AuthResponse['user'] }>('/auth/verify-email', { token });
        },
        onSuccess: (data) => {
            if (data?.user) {
                setUser(data.user);
                queryClient.setQueryData(authKeys.me(), data.user);
            }
            navigate(redirectTo, { replace: true });
        },
    });
}

// R24: request a fresh verification link (always succeeds silently to
// avoid leaking which addresses are registered).
export function useResendVerification() {
    return useMutation({
        mutationFn: async (email: string) => {
            return apiPost<{ message: string }>('/auth/resend-verification', { email });
        },
    });
}

export function useForgotPassword() {
    return useMutation({
        mutationFn: async (data: ForgotPasswordInput) => {
            return apiPost<{ message: string }>('/auth/forgot-password', data);
        },
    });
}

export function useResetPassword() {
    return useMutation({
        mutationFn: async (data: ResetPasswordInput & { token: string }) => {
            return apiPost<{ message: string }>('/auth/reset-password', {
                token: data.token,
                newPassword: data.newPassword,
            });
        },
    });
}

// Logout mutation
export function useLogout() {
    const queryClient = useQueryClient();
    const { logout } = useAuthStore();
    const navigate = useNavigate();
    const { toast } = useToast();

    return useMutation({
        mutationFn: async () => {
            return apiPost<{ message: string }>('/auth/logout');
        },
        onSuccess: () => {
            logout();
            queryClient.clear();
            toast({
                title: 'Signed out',
                description: 'You have been signed out successfully',
            });
            navigate('/login');
        },
        onError: () => {
            // Even if the API call fails, clear local state
            logout();
            queryClient.clear();
            navigate('/login');
        },
    });
}

// Fetch user sessions
export function useSessions() {
    return useQuery({
        queryKey: authKeys.sessions(),
        queryFn: async () => {
            const response = await apiGet<{ sessions: Session[] }>('/auth/sessions');
            return response.sessions;
        },
    });
}

// Revoke a session
export function useRevokeSession() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: async (sessionId: string) => {
            return apiDelete<{ message: string }>(`/auth/sessions/${sessionId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authKeys.sessions() });
            toast({
                title: 'Session revoked',
                description: 'The session has been terminated',
            });
        },
        onError: (error: Error) => {
            const message =
                error instanceof ApiClientError
                    ? error.message
                    : 'Failed to revoke session';
            toast({
                title: 'Error',
                description: message,
                variant: 'destructive',
            });
        },
    });
}

// Revoke all sessions
export function useRevokeAllSessions() {
    const queryClient = useQueryClient();
    const { logout } = useAuthStore();
    const navigate = useNavigate();
    const { toast } = useToast();

    return useMutation({
        mutationFn: async () => {
            return apiDelete<{ message: string }>('/auth/sessions');
        },
        onSuccess: () => {
            logout();
            queryClient.clear();
            toast({
                title: 'All sessions revoked',
                description: 'You have been signed out from all devices',
            });
            navigate('/login');
        },
        onError: (error: Error) => {
            const message =
                error instanceof ApiClientError
                    ? error.message
                    : 'Failed to revoke sessions';
            toast({
                title: 'Error',
                description: message,
                variant: 'destructive',
            });
        },
    });
}

// Update password
export function useUpdatePassword() {
    const { toast } = useToast();

    return useMutation({
        mutationFn: async (data: UpdatePasswordInput) => {
            return apiPut<{ message: string }>('/auth/password', data);
        },
        onSuccess: () => {
            toast({
                title: 'Password updated',
                description: 'Your password has been changed successfully',
            });
        },
        onError: (error: Error) => {
            const message =
                error instanceof ApiClientError
                    ? error.message
                    : 'Failed to update password';
            toast({
                title: 'Error',
                description: message,
                variant: 'destructive',
            });
        },
    });
}

// Update profile
export function useUpdateProfile() {
    const queryClient = useQueryClient();
    const { setUser } = useAuthStore();
    const { toast } = useToast();

    return useMutation({
        mutationFn: async (data: UpdateProfileInput) => {
            return apiPatch<User>('/auth/profile', data);
        },
        onSuccess: (user) => {
            setUser(user);
            queryClient.setQueryData(authKeys.me(), user);
            queryClient.invalidateQueries({ queryKey: authKeys.me() });
            toast({
                title: 'Success',
                description: 'Your profile has been updated',
            });
        },
        onError: (error: Error) => {
            const message =
                error instanceof ApiClientError
                    ? error.message
                    : 'An unexpected error occurred';
            toast({
                title: 'Update failed',
                description: message,
                variant: 'destructive',
            });
        },
    });
}

// OAuth providers
interface OAuthProviders {
    discord: boolean;
    google: boolean;
}

export function useOAuthProviders() {
    return useQuery({
        queryKey: ['oauth', 'providers'],
        queryFn: async () => {
            return apiGet<OAuthProviders>('/oauth/providers');
        },
        staleTime: 60 * 60 * 1000, // 1 hour
    });
}
